/**
 * Calculates all totals for vendor invoices. Included in the VendorInvoice
 * service and includes the service for vendor invoice item totals
 */
export function VendorInvoiceTotalsService (
  bigDecimalApi,
  sessionObjAPI,
  VendorInvoiceItemTotals
) {

  var VendorInvoiceTotals = function (invoice) {
    this.company     = sessionObjAPI.company();
    this.hasGroupTax = false;
    this.info        = sessionObjAPI.currency();
    this.invoice     = invoice;
    this.itemTotals  = new VendorInvoiceItemTotals(invoice);
  };

  /**
   * Determines whether or not the invoice is closed based on status
   * @return {boolean} true if closed, false if open
   */
  VendorInvoiceTotals.prototype.closed = function () {
    var self = this;

    return self.invoice.invoice_status === 'P' || self.invoice.invoice_status === 'C';
  };

  /**
   * If the flag "calculate_tax_on_subtotal" is set, then tax needs to be calculated
   * at the subtotal level, not just the total of all the item's tax calclulations.
   * This handles that form of calculations
   * @return {object} The Big Decimal value for the calculated tax
   */
  VendorInvoiceTotals.prototype.calculateTaxBySubtotal = function () {
    var self = this;
    var subtotal = BigDecimal.ZERO;
    var tax;
    var taxRate = new BigDecimal(self.company.purchases_tax_rate).movePointLeft(2);

    // loop through the items and get the total of them, without taxes
    angular.forEach(self.invoice.vendor_invoice_items_attributes, function (item){
      var cost;
      var unit_cost = BigDecimal.ZERO;
      var quantity = BigDecimal.ZERO;

      // We only need the items where tax is included
      if (!item.gst_free) {
        cost = self.invoice.price_includes_tax ? item.cost_including_tax : item.unit_cost;
        unit_cost = new BigDecimal( cost ? cost.toString(): '0' ).setScale(4, BigDecimal.ROUND_HALF_UP);
        quantity = new BigDecimal( item.quantity && item.quantity !== '-' ? item.quantity.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);
        subtotal = subtotal.add(unit_cost.multiply(quantity));
      }
    });

    // Now let's calculate the tax based on the subtotal of items

    // If the price includes tax, extract the tax from the total
    if (self.invoice.price_includes_tax) {
      tax = subtotal
        .subtract( subtotal.divide( BigDecimal.ONE.add(taxRate) ) )
          .setScale(2, BigDecimal.ROUND_HALF_UP);
    }

    // If not, calculate the tax based on the tax rate
    else {
      tax = subtotal
        .multiply(taxRate)
          .setScale(2, BigDecimal.ROUND_HALF_UP);
    }

    return tax;
  };

  /**
   * Calculates the balance due for the whole invoice
   * @return {string} the balance due for the invoice
   */
  VendorInvoiceTotals.prototype.balance = function () {
    var self = this;
    var total = new BigDecimal( self.invoice.savedTotal.toString() ).setScale(2, BigDecimal.ROUND_HALF_UP);
    var balance = new BigDecimal( self.invoice.savedBalance.toString() ).setScale(2, BigDecimal.ROUND_HALF_UP);
    var totalCalc = new BigDecimal( self.total() ).setScale(2, BigDecimal.ROUND_HALF_UP);

    // If the invoice is close, return what is sent from the server
    if (self.closed()) {
      return self.invoice.balance_due ? self.invoice.balance_due.toString(): '0';
    }

    // If the balance due is zero and not closed, then it is new so return the total
    if (balance.isZero()) {
      return totalCalc.toString();
    }

    // If we have a balance from the server, use it to calculate the new balance
    else {
      return totalCalc.subtract(total).add(balance).toString();
    }
  };

  VendorInvoiceTotals.prototype.getFreightTax = function () {
    const self = this;

    // WS-5448: freight GST applies only when freight is taxed (mirrors BE ui_aligned_totals).
    // Without this guard the screen double-counts freight GST on tax-freight-OFF invoices.
    if (!self.company.tax_freight) {
      return BigDecimal.ZERO.setScale(2, BigDecimal.ROUND_HALF_UP);
    }

    const freight = bigDecimalApi.get(self.invoice.freight, 2);
    const taxRate = bigDecimalApi.get(self.company.purchases_tax_rate).movePointLeft(2);

    return self.invoice.price_includes_tax
      ? freight.subtract( freight.divide( BigDecimal.ONE.add(taxRate) ) ).setScale(2, BigDecimal.ROUND_HALF_UP)
      : freight.multiply(taxRate).setScale(2, BigDecimal.ROUND_HALF_UP);
  };

  /**
   * Calculates the tax for the whole invoice
   * @return {string} The total tax for the invoice
   */
  VendorInvoiceTotals.prototype.tax = function () {
    const self = this;

    let tax = BigDecimal.ZERO;

    // If the invoice is closed, return the amount from the server
    if (self.closed()) {
      return self.invoice.gst || '0';
    }

    // If the flag is set, then we need to calculate the tax based on the subtotal (invoice level)
    else if (self.company.calculate_tax_on_subtotal) {
      tax = self.calculateTaxBySubtotal();
    }

    // If not, just add up the total taxes for each item (item level)
    else {
      angular.forEach(self.invoice.vendor_invoice_items_attributes, item => {
        const taxCalc = bigDecimalApi.get(self.itemTotals.tax(item), 2);

        tax = tax.add(taxCalc);
      });
    }

    // add freight
    return tax.add(self.getFreightTax()).toString();
  };

  /**
   * Calculates the subtotal for the invoice
   * @return {string} The subtotal amount
   */
  VendorInvoiceTotals.prototype.subtotal = function () {
    const self = this;

    const taxRate = new BigDecimal(self.company.purchases_tax_rate).movePointLeft(2);

    let subtotal     = BigDecimal.ZERO;
    let total        = BigDecimal.ZERO;
    let tax          = BigDecimal.ZERO;
    let taxableTotal = BigDecimal.ZERO;

    // If the invoice is closed, no need to do any calculations
    if (self.closed()) {
      return self.invoice.subtotal
        ? self.invoice.subtotal.toString()
        : '0';
    }

    // If the price includes tax then we need to extract the tax from each item for our subtotal
    else if (self.invoice.price_includes_tax && !self.company.calculate_tax_on_subtotal) {
      // Calculate items subtotal
      angular.forEach(self.invoice.vendor_invoice_items_attributes, item => {
        const taxCalc   = bigDecimalApi.get(self.itemTotals.tax(item));
        const itemTotal = bigDecimalApi.get(self.itemTotals.total(item));

        tax   = tax.add(taxCalc);
        total = total.add(itemTotal);
      });

      // WS-5448: the freight principal is always part of the total; its GST is added only when
      // freight is taxed (getFreightTax returns 0 otherwise). Mirrors BE ui_aligned_totals.
      const freight = bigDecimalApi.get(self.invoice.freight, 2);

      total = total.add(freight);
      tax   = tax.add(self.getFreightTax());

      subtotal = total.subtract(tax);
    }

    // if the calculate tax flag is set, we need to get the tax out on the invoice level
    else if (self.invoice.price_includes_tax && self.company.calculate_tax_on_subtotal) {
      angular.forEach(self.invoice.vendor_invoice_items_attributes, item => {
        const itemTotal = bigDecimalApi.get( self.itemTotals.total(item) );

        total = total.add(itemTotal);

        if (!item.gst_free) {
          taxableTotal = taxableTotal.add(itemTotal);
        }
      });

      tax = taxableTotal
        .subtract( taxableTotal.divide( BigDecimal.ONE.add(taxRate) ) )
          .setScale(2, BigDecimal.prototype.ROUND_HALF_UP);

      // WS-5448: the freight principal is always part of the total; its GST is added only when
      // freight is taxed (getFreightTax returns 0 otherwise). Mirrors BE ui_aligned_totals.
      const freight    = bigDecimalApi.get(self.invoice.freight, 2);
      const freightTax = self.getFreightTax();

      total = total.add(freight);
      tax   = tax.add(freightTax);

      subtotal = total.subtract(tax);

    }

    // If not, just add up each items total before taxes
    else {
      angular.forEach(self.invoice.vendor_invoice_items_attributes, function (item){
        var unit_cost = new BigDecimal( item.unit_cost ? item.unit_cost.toString(): '0' ).setScale(4, BigDecimal.ROUND_HALF_UP);
        var quantity = new BigDecimal( item.quantity ? item.quantity.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);

        subtotal = subtotal.add( unit_cost.multiply(quantity) );
      });
    }

    return subtotal ? subtotal.toString(): '0';
  };

  /**
   * Calculate the total of the invoice
   * @return {string} The total for the invoice
   */
  VendorInvoiceTotals.prototype.total = function () {
    const self = this;

    const freight  = bigDecimalApi.get(self.invoice.freight, 2);
    const rounding = bigDecimalApi.get(self.invoice.rounding, 2);
    const tax      = bigDecimalApi.get(self.tax(), 2);
    const total    = bigDecimalApi.get(self.subtotal(), 2);

    // If the invoice is closed, no need to do the calculation
    if (self.closed()) {
      return self.invoice.total ? self.invoice.total.toString(): '0';
    }

    let finalTotal = total.add(tax).add(rounding);

    if (!self.invoice.price_includes_tax) {
      finalTotal = finalTotal.add(freight);
    }

    return finalTotal.toString();
  };

  return VendorInvoiceTotals;
}